import { useSearchTableScope } from '@aibees/elephant/context/search-table-scope';
import { ROUTES } from '@aibees/elephant/router/routes';
import { useSearchHistoryStore } from '@aibees/elephant/store/search-history-store';
import type { SearchTableScope } from '@aibees/elephant/store/search-params-store';
import {
  SEARCH_PARAMS_REHYDRATED_EVENT,
  useSearchParamsStore,
} from '@aibees/elephant/store/search-params-store';
import { objSanitizeNilValues } from '@aibees/shared/utils';
import { isEqual } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SearchBrowseParams } from '../../types';
import {
  SAVED_SEARCH_URL_PARAM,
  SHARE_SEARCH_URL_PARAM,
  omitNonFilterParams,
} from './constants';

declare let window: Window & { __searchParamsStoreRehydrated?: boolean };

function parseSearchToParams(search: string): Partial<SearchBrowseParams> {
  const entries = new URLSearchParams(search);
  return omitNonFilterParams(
    Object.fromEntries(entries)
  ) as Partial<SearchBrowseParams>;
}

/**
 * Syncs the search params store (for the current scope) with the URL, ONE WAY.
 *
 * The store (persisted to per-tab `sessionStorage`) is the single source of
 * truth. Filter params are NEVER written back to the URL — that is what used to
 * push the full filter set into the query string and could exceed the web
 * server's URL length limit (HTTP 414). Instead:
 *
 * - **URL → store (read once, then strip):** on load, on router navigation, and
 *   on back/forward, if the URL carries filter params they are hydrated into the
 *   store once and then stripped from the URL. This keeps bookmarked/legacy
 *   filter links working and lets short "transport" links (labels, lists,
 *   exports, tag chips, etc.) still hand off filter state — the URL ends up clean.
 * - **Empty URL → no-op:** the store / sessionStorage wins, so returning to a
 *   search table restores the last filters without any URL params.
 *
 * Non-filter params (`search-id`, `saved-search`) are excluded from the sync via
 * `omitNonFilterParams` and are consumed + stripped by their own loaders
 * (`useApplySharedSearch`, `useApplySavedSearch`), which latch their id on mount.
 *
 * The per-scope search history store (undo/redo) is still fed from store changes
 * here — it is independent of the URL, so the header undo/redo keeps working.
 */
export function SearchParamsUrlSync({
  children,
}: {
  children: React.ReactNode;
}) {
  const scope = useSearchTableScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const paramsByScope = useSearchParamsStore.useParamsByScope();
  const skipHistoryForNextSync =
    useSearchParamsStore.useSkipHistoryForNextSync();
  const replaceParamsForScope = useSearchParamsStore.useReplaceParamsForScope();
  const clearSkipHistoryForNextSync =
    useSearchParamsStore.useClearSkipHistoryForNextSync();
  const addToHistory = useSearchHistoryStore.useAdd();

  const paramsRef = useRef<Partial<SearchBrowseParams>>({});
  const skipRef = useRef(skipHistoryForNextSync);
  const setSearchParamsRef = useRef(setSearchParams);
  const isHydratingRef = useRef(false);
  const hasRunPostRehydrateRef = useRef(false);
  // Stable refs so the URL watcher effect can read current values
  // without listing them as dependencies (avoids firing on store changes).
  const hasScopeRef = useRef(false);
  const hasRehydratedRef = useRef(false);
  const scopeRef = useRef(scope);
  const replaceParamsForScopeRef = useRef(replaceParamsForScope);

  setSearchParamsRef.current = setSearchParams;
  replaceParamsForScopeRef.current = replaceParamsForScope;
  scopeRef.current = scope;

  const hasScope =
    scope === 'company' || scope === 'people' || scope === 'employees';
  hasScopeRef.current = hasScope;
  paramsRef.current = hasScope ? paramsByScope[scope] ?? {} : {};
  skipRef.current = skipHistoryForNextSync;

  const scopeParams = hasScope
    ? paramsByScope[scope as SearchTableScope]
    : undefined;

  const [hasRehydrated, setHasRehydrated] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.__searchParamsStoreRehydrated === true
  );
  hasRehydratedRef.current = hasRehydrated;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => setHasRehydrated(true);
    window.addEventListener(SEARCH_PARAMS_REHYDRATED_EVENT, handler);
    return () =>
      window.removeEventListener(SEARCH_PARAMS_REHYDRATED_EVENT, handler);
  }, []);

  /**
   * Remove filter params from the URL after they have been hydrated into the
   * store, preserving the non-filter loader params (`search-id`, `saved-search`)
   * so their own loaders can still resolve + strip them.
   */
  const stripFilterParams = useCallback(() => {
    setSearchParamsRef.current(
      (prev) => {
        const next = new URLSearchParams();
        const shareId = prev.get(SHARE_SEARCH_URL_PARAM);
        const savedSearch = prev.get(SAVED_SEARCH_URL_PARAM);
        if (shareId) next.set(SHARE_SEARCH_URL_PARAM, shareId);
        if (savedSearch) next.set(SAVED_SEARCH_URL_PARAM, savedSearch);
        return next;
      },
      { replace: true }
    );
  }, []);

  /** Hydrate the current scope from the URL (if it has filter params), then strip. */
  const hydrateCurrentScopeFromUrl = useCallback(() => {
    const parsed = parseSearchToParams(window.location.search || '');
    if (Object.keys(parsed).length === 0) return;
    isHydratingRef.current = true;
    replaceParamsForScopeRef.current(
      scopeRef.current as SearchTableScope,
      parsed
    );
    stripFilterParams();
    // queueMicrotask fires after all effects in this commit but before
    // React's next re-render, ensuring isHydratingRef is cleared reliably.
    queueMicrotask(() => {
      isHydratingRef.current = false;
    });
  }, [stripFilterParams]);

  // After persist rehydration (once): if the URL has filter params, hydrate the
  // current scope from them and strip. If the URL is empty, the store wins.
  // company/people only; employees is handled by its own effect below.
  useEffect(() => {
    if (
      !hasRehydrated ||
      !hasScope ||
      hasRunPostRehydrateRef.current ||
      scope === 'employees'
    )
      return;
    hasRunPostRehydrateRef.current = true;
    hydrateCurrentScopeFromUrl();
  }, [hasRehydrated, hasScope, scope, hydrateCurrentScopeFromUrl]);

  // employees scope: hydrate from URL only when the URL has filter params, so we
  // don't overwrite programmatic params (e.g. `company_id`, owned by
  // `useEmployeesScopeOwnership` — see docs/search-filters-url-store-sync.md).
  useEffect(() => {
    if (scope !== 'employees') return;
    hydrateCurrentScopeFromUrl();
  }, [scope, hydrateCurrentScopeFromUrl]);

  // Store → history (undo/redo): when params for this scope change, push the
  // snapshot onto the per-scope history stack (unless this change came from an
  // undo/redo replay or a URL hydration). Independent of the URL.
  useEffect(() => {
    if (!hasScope || isHydratingRef.current) return;
    const sanitized = objSanitizeNilValues(paramsRef.current) as Record<
      string,
      string
    >;
    if (!skipRef.current) {
      addToHistory(scope as SearchTableScope, sanitized);
      clearSkipHistoryForNextSync();
    }
  }, [hasScope, scope, scopeParams, addToHistory, clearSkipHistoryForNextSync]);

  const handlePopState = useCallback(() => {
    const { pathname, search } = window.location;

    // Derive scope from the actual URL, not stale React state.
    let currentScope: SearchTableScope | undefined;
    if (pathname.startsWith(ROUTES.searchCompany)) currentScope = 'company';
    else if (pathname.startsWith(ROUTES.searchPeople)) currentScope = 'people';
    else if (pathname.startsWith(ROUTES.companyEmployees))
      currentScope = 'employees';

    if (!currentScope) return;
    const parsed = parseSearchToParams(search || '');
    if (Object.keys(parsed).length === 0) return;
    isHydratingRef.current = true;
    replaceParamsForScope(currentScope, parsed);
    stripFilterParams();
    queueMicrotask(() => {
      isHydratingRef.current = false;
    });
  }, [replaceParamsForScope, stripFilterParams]);

  useEffect(() => {
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [handlePopState]);

  // URL → store watcher: when React Router's searchParams change (navigation,
  // link click, navigate(), refresh) and the URL carries filter params, hydrate
  // the store from them and strip. Empty (filter-wise) URL is a no-op — the store
  // is the source of truth. Uses only searchParams as a trigger dep; everything
  // else is read via refs to avoid firing on store changes.
  useEffect(() => {
    if (
      !hasScopeRef.current ||
      !hasRehydratedRef.current ||
      isHydratingRef.current
    )
      return;

    const urlParams = omitNonFilterParams(
      Object.fromEntries(searchParams.entries())
    ) as Record<string, string>;

    // No filter params in the URL → nothing to consume; keep the store as-is.
    if (Object.keys(urlParams).length === 0) return;

    isHydratingRef.current = true;
    const storeParams = objSanitizeNilValues(paramsRef.current) as Record<
      string,
      string
    >;
    if (!isEqual(urlParams, storeParams)) {
      replaceParamsForScopeRef.current(
        scopeRef.current as SearchTableScope,
        urlParams as Partial<SearchBrowseParams>
      );
    }
    stripFilterParams();
    queueMicrotask(() => {
      isHydratingRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return <>{children}</>;
}
